import React, { useContext, useEffect, useState } from "react";
import { nanoid } from "nanoid";
import Button from "../../../Button";
import IconButton from "../../../CommonComponents/IconButton";
import InputField from "../../../CommonComponents/InputField";
import Radio from "../../../CommonComponents/Radio";
import RichTextEditor from "../../../CommonComponents/RichTextEditor";
import TopicComplexity from "../TopicComplexity";
import useAxiosPrivate from "../../../../Hooks/useAxiosPrivate";
import Tooltip from "../../../CommonComponents/Tooltip";
import useCommonFunctionHooks from "../../../../Hooks/useCommonFunctionHooks";
import constants, { RESULT_STATUS } from "../../../../constants";
import AlertContext from "../../../../context/AlertContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ReusableBreadcrumbs from "../../../CommonComponents/ReusableBreadCrumbs";
import { getDataFromStorage } from "../../../../util";
import Loading from "../../../Notification-Loading/Loading/Loading";
import Toast from "../../../CommonComponents/Toast";
import InputDropdown from "../../../CommonComponents/InputDropdown";

// const UploadIcon = ({ stroke = "#344054" }) => (
//   <svg
//     width="17"
//     height="17"
//     viewBox="0 0 17 17"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//   >
//     <path
//       d="M15.8359 10.835V11.835C15.8359 13.2351 15.8359 13.9352 15.5635 14.4699C15.3238 14.9403 14.9413 15.3228 14.4709 15.5625C13.9361 15.835 13.2361 15.835 11.8359 15.835H4.83594C3.43581 15.835 2.73574 15.835 2.20096 15.5625C1.73056 15.3228 1.3481 14.9403 1.10842 14.4699C0.835938 13.9352 0.835938 13.2351 0.835938 11.835V10.835M12.5026 6.66829L8.33594 10.835M8.33594 10.835L4.16927 6.66829M8.33594 10.835V0.834961"
//       stroke={stroke}
//       strokeWidth="1.67"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//   </svg>
// );

const validator = {
  name: (value) => (!value || value.trim() === "" ? "Name is required" : null),
  isMultiAnswer: (value) =>
    typeof value !== "boolean" ? "Please select an option" : null,
  answers: (answers, isMultiAnswer) => {
    if (!answers || answers.length === 0 || answers.length <= 1) {
      return "At least two option is required";
    }
    const hasEmptyAnswerText = answers.some(
      (answer) => !answer.answerText || answer.answerText.trim() === "",
    );

    if (hasEmptyAnswerText) {
      return "Answer Text is required";
    }
    const correctAnswers = answers.filter((answer) => answer.isCorrect);
    if (isMultiAnswer) {
      if (correctAnswers.length < 1) {
        return "At least one correct answer must be selected for multiple answer questions";
      }
    } else {
      if (correctAnswers.length !== 1) {
        return "Exactly one correct answer must be selected for single answer questions";
      }
    }
    return null;
  },
  topicComplexity: (value) => {
    if (!value || value.length === 0) {
      return "At least one topic and complexity must be selected";
    } else {
      for (let i = 0; i < value.length; i++) {
        if (!value[i].topicId || !value[i].complexityId) {
          return "Both topic and complexity are required for each entry";
        }
      }
    }
    return null;
  },
};

const McqQuestionEntry = () => {
  const axios = useAxiosPrivate();
  const useCommonFunctionHks = useCommonFunctionHooks();
  const Alert = useContext(AlertContext);
  const { type, questionid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const isFixedQns = searchParams.get("fixedqns");
  const subId = searchParams.get("subId");
  const topId = searchParams.get("topId");

  const navigate = useNavigate();
  const [questionDetail, setQuestionDetail] = useState({
    name: "",
    description: "",
    isMultiAnswer: false,
    answers: [
      {
        id: nanoid(),
        answerText: "",
        isCorrect: false,
      },
    ],
    ansexp: "",
    topicComplexity: [],
  });

  const [errors, setErrors] = useState({});
  const [topics, setTopics] = useState([]);
  const [subject, setSubject] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [complexity, setComplexity] = useState([]);
  const [topicComplexity, setTopicComplexity] = useState([]);
  const [dataIsLoading, setDataIsLoading] = useState(false);

  const [toastType, setToastType] = useState("warning");
  const [openToast, setOpenToast] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  //setting up for edit scenario
  const buildTopicComplexity = (topicDataStr, topics, complexity) => {
    if (!topicDataStr) return [];

    let parsed = [];
    try {
      parsed = JSON.parse(topicDataStr);
    } catch (e) {
      console.error("Invalid topicData JSON", e);
      return [];
    }
    return parsed
      .map(({ topicid, complexityid }) => {
        const topic = topics.find((t) => t.value === topicid);
        const comp = complexity.find((c) => c.value === complexityid);
        if (!topic || !comp) return null;

        return {
          id: nanoid(),
          topicId: topicid,
          topicName: topic.label,
          complexityId: complexityid,
          complexityName: comp.label,
        };
      })
      .filter(Boolean);
  };

  const buildSubject = (topicDataStr, subjects) => {
    if (!topicDataStr) return [];
    let parsed = [];
    try {
      parsed = JSON.parse(topicDataStr);
    } catch (e) {
      console.error("Invalid topicData JSON", e);
      return [];
    }
    return parsed[0].subject_id;
  }

  useEffect(() => {
    const fetchTopics = async (subject_id) => {
      try {
        const [topics, complexity] = await Promise.all([
          axios.get(`node/admin/topic/get/0?subjectId=${subject_id}`),
          axios.get("node/admin/complexity/get"),
        ]);

        let editTopics = []
        if (topics.data.resultCode === RESULT_STATUS.SUCCESS) {
          editTopics = topics.data.data?.map((topic) => ({
            value: topic.id,
            label: topic.name,
          }));
          setTopics(
            topics.data.data?.map((topic) => ({
              value: topic.id,
              label: topic.name,
            })),
          );
        }
        let editComplexity = complexity.data?.map((comp) => ({
          value: comp.id,
          label: comp.name,
        }));
        setComplexity(
          complexity.data?.map((comp) => ({
            value: comp.id,
            label: comp.name,
          })),
        );
        return { editTopics, editComplexity };
      } catch (error) {
        console.error("Error fetching topics and complexity:", error);
      }
    };
    const getQuestionDetails = async () => {
      try {
        setDataIsLoading(true);
        if (getDataFromStorage("role") === constants.Roles.tutor)
          Alert.setPreview(true);
        // Determine the endpoint based on the `id` value

        const endpoint = `node/admin/content/${questionid}`;

        console.log("endPoint:", endpoint);

        const response = await axios.get(
          endpoint,
          useCommonFunctionHks.getHeaderDetailsForAxios(),
        );

        let json = response.data;
        let newData;

        newData = json.data;

        newData.qnId = questionid;
        newData.published = 0;
        // Alert.setIsFixedQn(data.isFixedqn);

        const subjects = await fetchSubjects();
        setSubjects(subjects);
        const { editTopics, editComplexity } = await fetchTopics(JSON.parse(newData.topicData)[0].subject_id);
        const normalizeAnswers = (answers = []) =>
          answers.map((ans) => ({
            id: ans.id ?? nanoid(), // preserve id if already exists
            answerText: ans.answerText,
            isCorrect: Boolean(ans.isCorrect),
          }));
        //topicCompexity
        setQuestionDetail((prev) => {
          const updatedAnswers = normalizeAnswers(newData.answers ?? []);
          const baseTopicComplexity = buildTopicComplexity(
            newData.topicData,
            editTopics,
            editComplexity,
          );
          // console.log("baseTopic:", baseTopicComplexity);

          const subject = buildSubject(newData.topicData, subjects);
          setSubject(subject);


          const type = newData.type;
          let finalTopicComplexity = baseTopicComplexity;
          setTopicComplexity(finalTopicComplexity);
          return {
            ...prev,
            name: newData.name,
            description: newData.description,
            answers: updatedAnswers,
            ansexp: newData.ansexp,
            topicComplexity: finalTopicComplexity,
            isMultiAnswer: type === constants.libTypeIds.MCQ_MULTI,
          };
        });
      } catch (error) {
        useCommonFunctionHks.getCatchBlockDetails(error);
        // setQueLoading(true);
      } finally {
        setDataIsLoading(false);
      }
    };

    questionid && getQuestionDetails();
  }, [questionid]);

  //initail call
  useEffect(() => {
    !questionid && fetchSubjects();
  }, [axios]);

  useEffect(() => {
    subject && fetchTopics();
  }, [subject]);

  useEffect(() => {
    fetchComplexities();
  }, []);

  const fetchSubjects = async () => {
    try {
      setDataIsLoading(true);
      const response = await axios.get(`node/subject?type=2&isShared=false`);
      if (response.data.resultCode !== RESULT_STATUS.SUCCESS) {
        throw new Error("Something went wrong while fetching");
      }
      let subjectsData = response.data.data ?? [];
      subjectsData = subjectsData.map((topic) => ({ value: topic.id, label: topic.name }));
      setSubjects(subjectsData);
      if (+subId && subjectsData.length) {
        setSubject(+subId);
      }
      return subjectsData;
    } catch (error) {
      console.error("Error fetching Subjects:", error);
      setOpenToast(true);
      setErrMsg(`${error}`);
    }
    finally {
      setDataIsLoading(false);
    }
  }

  const fetchComplexities = async () => {
    try {
      setDataIsLoading(true);
      const complexity = await axios.get("node/admin/complexity/get");
      console.log("complexity.data.data?:", complexity.data);
      setComplexity(
        complexity.data?.map((comp) => ({
          value: comp.id,
          label: comp.name,
        })),
      );
    } catch (error) {
      console.error("Error fetching complexity:", error);
      setOpenToast(true);
      setErrMsg(`${error}`);
    }
    finally {
      setDataIsLoading(false);
    }
  }

  const fetchTopics = async () => {
    // console.log("fetching Topics:", subject);
    try {
      setDataIsLoading(true);
      if (!subject || subject.length === 0) {
        return;
      }
      const topics = await axios.get(`node/admin/topic/get/0?subjectId=${subject}`);
      if (topics.data.resultCode === RESULT_STATUS.SUCCESS)
        setTopics(
          topics.data?.data?.map((topic) => ({ value: topic.id, label: topic.name })),
        );
    } catch (error) {
      console.error("Error fetching topics:", error);
      setOpenToast(true);
      setErrMsg(`${error}`);
    }
    finally {
      setDataIsLoading(false);
    }
  };

  const onChangeQuestionDetail = (field, value) => {
    // console.log(field, value);
    if (field === "isMultiAnswer") {
      resetAnswers();
    }
    const error = validator[field]
      ? validator[field](value, questionDetail.isMultiAnswer)
      : null;
    setQuestionDetail((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  // console.log("Props:", Props);

  console.log("questionDetais:", questionDetail);

  const onChangeAnwerDetail = (index, field, value) => {
    const updatedAnswers = questionDetail.answers.map((answer, idx) => {
      if (answer.id === index) {
        return {
          ...answer,
          [field]: value,
        };
      } else {
        return answer;
      }
    });

    setQuestionDetail((prev) => ({
      ...prev,
      answers: updatedAnswers,
    }));
  };

  const onAddAnswer = () => {
    const newAnswer = {
      id: nanoid(),
      answerText: "",
      isCorrect: false,
    };

    setQuestionDetail((prev) => ({
      ...prev,
      answers: [...prev.answers, newAnswer],
    }));
  };

  const onClickAnswerCorrect = (id) => {
    // let filtered = questionDetail.answers.filter((ans) => ans.id === id);
    // if (filtered?.[0]?.answerText?.trim() === "") {
    //   const error = {
    //     answer: "Answer is required before marking it as correct answer",
    //   };
    //   setErrors(error);
    //   return;
    // }
    setQuestionDetail((prev) => {
      const updatedAnswers = prev.answers.map((answer) => {
        // Single answer (radio-like behavior)
        if (!prev.isMultiAnswer) {
          return {
            ...answer,
            isCorrect: answer.id === id,
          };
        }

        // Multi answer (checkbox-like behavior)
        if (answer.id === id) {
          return {
            ...answer,
            isCorrect: !answer.isCorrect,
          };
        }

        return answer;
      });

      return {
        ...prev,
        answers: updatedAnswers,
      };
    });
  };

  const onDeleteAnswer = (id) => {
    const filteredAnswers = questionDetail.answers.filter(
      (answer) => answer.id !== id,
    );

    setQuestionDetail((prev) => ({
      ...prev,
      answers: filteredAnswers,
    }));
  };

  const onAddTopicComp = (topicId, complexityId) => {
    const topic = topics.find((t) => t.value === topicId);
    const complexityLevel = complexity.find((c) => c.value === complexityId);

    if (!topic || !complexityLevel) return;
    const newTopicComplexity = {
      id: nanoid(),
      topicId: topic.value,
      topicName: topic.label,
      complexityId: complexityLevel.value,
      complexityName: complexityLevel.label,
    };

    setTopicComplexity((prev) => {
      const isAlreadyAdded = prev.some(
        (tc) =>
          tc.topicId === topic.value &&
          tc.complexityId === complexityLevel.value,
      );

      if (isAlreadyAdded) {
        // console.log("Topic and complexity already added");
        Alert.setShowNotify({
          show: true,
          title: "Warning",
          msg: `Topic "${topic?.label}" already added!`,
        });
        return prev;
      }

      return [...prev, newTopicComplexity];
    });

    setQuestionDetail((prev) => {
      const prevList = prev.topicComplexity ?? [];

      const isAlreadyAdded = prevList.some(
        (tc) =>
          tc.topicId === topic.value &&
          tc.complexityId === complexityLevel.value,
      );

      if (isAlreadyAdded) {
        return prev;
      }

      return {
        ...prev,
        topicComplexity: [...prevList, newTopicComplexity],
      };
    });

    setErrors((prev) => ({
      ...prev,
      topicComplexity: null,
    }));
  };

  // console.log("topicComplextiyt:", topicComplexity);

  const onRemoveTopicComp = (id) => {
    console.log("Remove topic complexity", id);
    setTopicComplexity((prev) => prev.filter((tc) => tc.id !== id));
  };

  // console.log("questionDetail", questionDetail);

  const onSave = async (e, edit) => {
    //validate all fields
    console.log("edit:", edit);
    const formData = new FormData();

    const nameError = validator.name(questionDetail.name);
    const isMultiAnswerError = validator.isMultiAnswer(
      questionDetail.isMultiAnswer,
    );
    const answersError = validator.answers(
      questionDetail.answers,
      questionDetail.isMultiAnswer,
    );
    const topicComplexityError = validator.topicComplexity(topicComplexity);

    const allErrors = {
      name: nameError,
      isMultiAnswer: isMultiAnswerError,
      answers: answersError,
      topicComplexity: topicComplexityError,
    };
    setErrors(allErrors);

    const hasErrors = Object.values(allErrors).some((error) => error !== null);
    if (!hasErrors) {
      //submit the form
      console.log("Form is valid. Submitting...", questionDetail);
      // console.log("topicAnDComplexity:", topicComplexity);
      let qdata = {
        ...questionDetail,
        topicData: topicComplexity,
        secData: topicComplexity.map((tc) => ({
          complexityId: tc.complexityId,
        })),
        complexity: topicComplexity.map((tc) => ({
          complexityId: tc.complexityId,
        })),
        subject: subject
      };
      if (edit && questionid) {
        qdata.id = questionid;
      }
      qdata = JSON.stringify({
        qdata,
      });
      const libtypename = questionDetail.isMultiAnswer
        ? "MCQ_MULTI"
        : "MCQ_SINGLE";
      formData.append("qdata", qdata);
      formData.append("libtypeid", questionDetail?.isMultiAnswer ? 9 : 8);
      formData.append("libtypename", libtypename);
      formData.append("mcqAns", JSON.stringify(questionDetail?.answers));

      try {
        setDataIsLoading(true);
        const response = await axios({
          method: "post",
          url: `node/admin/library/${edit ? "update" : "add"}`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        // console.log("response,", response);
        if (response.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
          Alert.setShowNotify({
            show: true,
            msg: `${response.data.message}`,
            title: "Success !",
          });
          // Alert.handleNavigateToLpAfterAddTest(newDetails);
          if (+subId && +topId) {
            navigate(`/library/2/subjects?subId=${subId}`);
          }
          else {
            navigate("/library/2/questionlist");
          }
        }
        if (
          response.data.resultCode === constants.RESULT_STATUS.TECHNICAL_ERROR
        ) {
          throw new Error(
            response.data.msg || `Something went wrong while ${edit ? "updating" : "adding"} MCQ`,
          );
        }
      } catch (error) {
        Alert.setShowNotify({
          show: true,
          msg: `${`Something went wrong while ${edit ? "updating" : "adding"} MCQ`}`,
          title: "Error !",
        });
      } finally {
        setDataIsLoading(false);
      }
    } else {
      console.log("Form has errors. Fix them before submitting.", allErrors);
      setOpenToast(true);
      setErrMsg("Please fill the highlighted fields.");
    }
  };

  const breadcrumbItems = [
    { label: "Library" },
    {
      label: `MCQ`,
      href: `${topId && subId ? `/library/${type}/subjects?subId=${+subId}&topId=${+topId}` : `/library/${type}/questionlist`}`,
    },
    { label: `${questionid ? "Edit" : "Add"} Question` },
  ];

  const resetAnswers = () => {
    setQuestionDetail((prev) => ({
      ...prev,
      answers: prev.answers.map((ans) => ({
        ...ans,
        isCorrect: false,
      })),
    }));
  };

  return (
    <main className="mcq-question-entry-container">
      {dataIsLoading && <Loading />}
      <Toast
        open={openToast}
        onClose={() => setOpenToast(false)}
        message={errMsg}
        type={toastType}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      // slots={{ transition: Slide }}
      />
      <ReusableBreadcrumbs items={breadcrumbItems} />
      <section className="mcq-question-entry-header">
        <h2 className="mcq-question-entry-header-title">
          {`${questionid ? "Edit " : "Add "}`}Question
        </h2>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {(getDataFromStorage("role") === constants.Roles.admin ||
            getDataFromStorage("role") === constants.Roles.creator ||
            getDataFromStorage("role") === constants.Roles.super_admin ||
            getDataFromStorage("role") === constants.Roles.trainer) &&
            questionid && (
              <Button
                size="sm"
                hierarchy={{
                  type: "primary",
                  buttonText: "Update",
                }}
                onClick={(e) => onSave(e, "edit")}
                disable={dataIsLoading}
              />
            )}
          {!questionid && (
            <Button
              size="sm"
              hierarchy={{
                type: "primary",
                buttonText: "Save",
              }}
              onClick={(e) => onSave(e)}
              disable={dataIsLoading}
            />
          )}
        </div>
      </section>
      <section className="mcq-question-entry-body">
        {/* Multiple upload */}
        {/* <div className="mcq-question-entry-multiple-upload">
          <div>
            <h3>Multiple Questions:</h3>
            <div>
              <p>Template for multiple questions</p>
              <IconButton
                size="sm"
                hierarchy="secondaryGrey"
                children={<UploadIcon />}
              />
            </div>
          </div>
          <div>
            <Button
              size="sm"
              hierarchy={{ type: "primary", buttonText: "Upload Questions" }}
              icon={{
                icon: (
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.8359 10.835V11.835C15.8359 13.2351 15.8359 13.9352 15.5635 14.4699C15.3238 14.9403 14.9413 15.3228 14.4709 15.5625C13.9361 15.835 13.2361 15.835 11.8359 15.835H4.83594C3.43581 15.835 2.73574 15.835 2.20096 15.5625C1.73056 15.3228 1.3481 14.9403 1.10842 14.4699C0.835938 13.9352 0.835938 13.2351 0.835938 11.835V10.835M12.5026 5.00163L8.33594 0.834961M8.33594 0.834961L4.16927 5.00163M8.33594 0.834961V10.835"
                      stroke="white"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
                type: "leading",
              }}
            />
            <p>You can upload multiple questions in a csv format.</p>
          </div>
        </div>
        <div className="mcq-question-entry-or">(Or)</div> */}
        {/* Single question */}
        <div className="mcq-question-entry-single-question">
          <h3 className="mcq-question-entry-single-question-title">
            Single Question:
          </h3>
          <div className="mcq-question-entry-single-question-input">
            <InputField
              label="Main Question"
              name="name"
              placeholder="Enter the heading here"
              value={questionDetail.name}
              onChange={(event) =>
                onChangeQuestionDetail("name", event.target.value)
              }
              hintText={errors.name}
              destructive={!!errors.name}
            />
            {/* description */}
            <RichTextEditor
              title="Description"
              name="description"
              value={questionDetail.description}
              onChange={(event) => onChangeQuestionDetail("description", event)}
            />
            {/* Topic and complexity */}
            <InputDropdown
              label="Subject"
              placeholder="Select subject"
              // creatable
              searchable
              value={subject}
              options={subjects ?? []}
              onChange={(value) => {
                setTopics([]);
                setSubject(value);
                setQuestionDetail((prev) => ({
                  ...prev,
                  topicComplexity: []
                }));
                setTopicComplexity([]);
              }}
              hintText={errors.subject}
              destructive={!!errors.subject}
              disabled={+subId}
            />
            <TopicComplexity
              onAddTopicComp={onAddTopicComp}
              topicOptions={topics}
              complexityOptions={complexity}
              topicComplexity={topicComplexity}
              onRemoveTopicComp={onRemoveTopicComp}
              errors={errors}
              topicId={+topId}
            />
            {/* Answers */}
            <div className="mcq-question-entry-single-question-input-answers">
              <p className="mcq-question-entry-single-question-input-answers-label">
                Answers
              </p>
              <div className="mcq-question-entry-single-question-input-answers-content">
                <div className="mcq-question-entry-single-question-input-answers-content-wrapper">
                  <p className="mcq-question-entry-single-question-input-answers-content-wrapper-label">
                    Select the no. of correct answers for this question :
                  </p>
                  <div className="mcq-question-entry-single-question-input-answers-content-wrapper-options">
                    <Radio
                      text="Single"
                      size="sm"
                      checked={!questionDetail.isMultiAnswer}
                      onChange={(checked) =>
                        onChangeQuestionDetail("isMultiAnswer", !checked)
                      }
                    />
                    <Radio
                      text="Multiple"
                      size="sm"
                      checked={questionDetail.isMultiAnswer}
                      onChange={(checked) =>
                        onChangeQuestionDetail("isMultiAnswer", checked)
                      }
                    />
                  </div>
                </div>
                <div className="mcq-question-entry-single-question-input-answers-content-answer-list">
                  {questionDetail?.answers.map((answer) => (
                    <div
                      className="mcq-question-entry-single-question-input-answers-content-answer-list-item"
                      key={answer.id}
                    >
                      <div className="mcq-question-entry-single-question-input-answers-content-answer-list-item-input-group">
                        <IconButton
                          size="sm"
                          hierarchy="secondaryGrey"
                          onClick={() => onClickAnswerCorrect(answer.id)}
                          className={`mcq-question-entry-single-question-input-answers-content-answer-list-item-input-group-correct-icon${answer.isCorrect ? " correct-answer" : ""
                            }`}
                          children={
                            <svg
                              width="16"
                              height="12"
                              viewBox="0 0 16 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M14.3333 1L5.16667 10.1667L1 6"
                                stroke="#667085"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        />
                        <InputField
                          placeholder="Answer"
                          name="answer"
                          className="mcq-question-entry-single-question-input-answers-content-answer-list-item-input-group-field"
                          value={answer.answerText}
                          onChange={(event) =>
                            onChangeAnwerDetail(
                              answer.id,
                              "answerText",
                              event.target.value,
                            )
                          }
                        />
                      </div>

                      <Tooltip title="Delete answer" isIcon={true}>
                        <IconButton
                          size="sm"
                          hierarchy="tertiaryGrey"
                          destructive={true}
                          onClick={() => onDeleteAnswer(answer.id)}
                          children={
                            <svg
                              width="17"
                              height="19"
                              viewBox="0 0 17 19"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M0.699219 4.03353H2.36589M2.36589 4.03353H15.6992M2.36589 4.03353V15.7002C2.36589 16.1422 2.54148 16.5661 2.85404 16.8787C3.1666 17.1913 3.59052 17.3669 4.03255 17.3669H12.3659C12.8079 17.3669 13.2318 17.1913 13.5444 16.8787C13.857 16.5661 14.0326 16.1422 14.0326 15.7002V4.03353H2.36589ZM4.86589 4.03353V2.36686C4.86589 1.92483 5.04148 1.50091 5.35404 1.18835C5.6666 0.87579 6.09052 0.700195 6.53255 0.700195H9.86589C10.3079 0.700195 10.7318 0.87579 11.0444 1.18835C11.357 1.50091 11.5326 1.92483 11.5326 2.36686V4.03353M6.53255 8.2002V13.2002M9.86589 8.2002V13.2002"
                                stroke="#667085"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        />
                      </Tooltip>
                      {answer?.isCorrect && (
                        <p className="mcq-question-entry-single-question-input-answers-content-answer-list-item-correct-answer">
                          Correct Answer
                        </p>
                      )}
                    </div>
                  ))}
                  <Button
                    size="sm"
                    className="mcq-question-entry-single-question-input-answers-content-add-answer-button"
                    hierarchy={{ type: "linkGrey", buttonText: "Add Answer" }}
                    onClick={onAddAnswer}
                    icon={{
                      icon: (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.83333 1V12.6667M1 6.83333H12.6667"
                            stroke="#667085"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ),
                      type: "leading",
                    }}
                  />
                  <p className="mcq-question-entry-single-question-error">
                    {errors.answers}
                  </p>
                </div>
              </div>
            </div>
            {/* ansexp  */}
            <RichTextEditor
              title="Explanation for the answer"
              name="ansexp"
              value={questionDetail.ansexp}
              onChange={(event) => onChangeQuestionDetail("ansexp", event)}
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default McqQuestionEntry;
